import React, { useEffect, useState } from "react";
import api from "../api";
import CanvasRenderer from "./CanvasRenderer";

const CANVAS_PREVIEW_CACHE = new Map();

/**
 * CanvasPreview - renders a preview of a canvas using CanvasRenderer.
 * It calculates tight bounds around the cards to maximize space usage.
 */
const CanvasPreview = ({
    canvasId,
    cards: initialCards,
    width,
    height,
    language = 'en',
    symbols = [],
    showImages = true,
    zoom = 1,
    padding = 0.1 // Tight internal padding
}) => {
    const [previewCards, setPreviewCards] = useState(initialCards || []);
    const [canvasName, setCanvasName] = useState('');
    const [loading, setLoading] = useState(!initialCards);

    useEffect(() => {
        if (initialCards) {
            setPreviewCards(initialCards);
            setLoading(false);
            return;
        }

        const fetchCanvasPreview = async () => {
            // Check cache first
            if (CANVAS_PREVIEW_CACHE.has(canvasId)) {
                const cached = CANVAS_PREVIEW_CACHE.get(canvasId);
                setPreviewCards(cached.cards);
                setCanvasName(cached.name || '');
                setLoading(false);
                return;
            }

            try {
                const res = await api.get(`/api/canvas/${canvasId}`);
                if (res.data && res.data.cards) {
                    CANVAS_PREVIEW_CACHE.set(canvasId, { cards: res.data.cards, name: res.data.canvas?.name });
                    setPreviewCards(res.data.cards);
                    setCanvasName(res.data.canvas?.name || '');
                }
            } catch (err) {
                console.error(`Failed to fetch canvas ${canvasId} for preview:`, err);
            } finally {
                setLoading(false);
            }
        };

        fetchCanvasPreview();
    }, [canvasId, initialCards]);

    if (loading) {
        return (
            <div style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#aaa',
                fontSize: 10,
            }}>
                Loading...
            </div>
        );
    }

    if (!previewCards.length) {
        return (
            <div style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#aaa',
                fontSize: 10,
            }}>
                No cards
            </div>
        );
    }

    // Calculate tight bounds of all cards
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const card of previewCards) {
        minX = Math.min(minX, card.pos_x);
        minY = Math.min(minY, card.pos_y);
        maxX = Math.max(maxX, card.pos_x + 1);
        maxY = Math.max(maxY, card.pos_y + 1);
    }

    // Map cards relative to bounds
    const bounds = {
        minX: minX - padding,
        minY: minY - padding,
        maxX: maxX + padding,
        maxY: maxY + padding
    };

    const mappedCards = previewCards.map(card => ({
        ...card,
        x: card.pos_x - bounds.minX,
        y: card.pos_y - bounds.minY,
        width: 1,
        height: 1,
        background: showImages ? (card.custom_background || card.default_background) : card.default_background
    }));

    // Calculate logical dimensions
    const logicalWidth = Math.max(0.1, bounds.maxX - bounds.minX);
    const logicalHeight = Math.max(0.1, bounds.maxY - bounds.minY);

    // Calculate scale to fit within the given width/height while maintaining aspect ratio
    const scale = Math.min(width / logicalWidth, height / logicalHeight);

    return (
        <div style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            position: 'relative',
        }}>
            <CanvasRenderer
                cards={mappedCards}
                width={logicalWidth}
                height={logicalHeight}
                mode="minimap"
                scale={scale}
                language={language}
                symbols={symbols}
                showImages={showImages}
            />
            {canvasName && (
                <span style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#222',
                    fontWeight: 900,
                    fontSize: 12 * zoom,
                    textAlign: 'center',
                    padding: 8 * zoom,
                    boxSizing: 'border-box',
                    wordBreak: 'break-word',
                    textShadow: '0 0 10px rgb(255, 255, 255), 0 0 5px rgb(255, 255, 255)',
                    zIndex: 5,
                    pointerEvents: 'none',
                }}>
                    {canvasName}
                </span>
            )}
        </div>
    );
};

export default CanvasPreview;
